/**
 * Merchant KYC Service - Handles merchant-specific KYC operations
 * Each merchant has an independent KYC profile with separate document storage and verification status
 */

class MerchantKycService {
  constructor() {
    this.storagePrefix = 'merchant_kyc_';
    this.documentsPrefix = 'merchant_docs_';
    this.verificationPrefix = 'merchant_verification_';
  }

  /**
   * Get merchant-specific storage key
   */
  getMerchantKey(merchantId, type = 'profile') {
    const prefixes = {
      profile: this.storagePrefix,
      documents: this.documentsPrefix,
      verification: this.verificationPrefix
    };
    return `${prefixes[type]}${merchantId}`;
  }

  /**
   * Initialize merchant KYC profile
   */
  initializeMerchantProfile(merchantId, merchantData = {}) {
    if (!merchantId) {
      throw new Error('Merchant ID is required');
    }
    
    const profileKey = this.getMerchantKey(merchantId, 'profile');
    
    const defaultProfile = {
      merchantId, // Ensure merchant ID is always set
      personalInfo: {
        fullName: merchantData.fullName || '',
        email: merchantData.email || '',
        phone: merchantData.phone || '',
        dateOfBirth: '',
        nationality: 'Indian',
        occupation: 'Business Owner'
      },
      businessInfo: {
        businessName: merchantData.businessName || '',
        businessType: '',
        registrationNumber: '',
        gstin: '',
        pan: '',
        registeredAddress: '',
        city: '',
        state: '',
        pincode: '',
        businessCategory: '',
        annualTurnover: ''
      },
      bankInfo: {
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        branchName: ''
      },
      kycStatus: {
        status: 'NOT_INITIATED',
        submittedAt: null,
        approvedAt: null,
        rejectedAt: null,
        rejectionReason: '',
        completionPercentage: 0,
        lastUpdated: new Date().toISOString()
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Check if profile already exists
    const existingProfile = this.getMerchantProfile(merchantId);
    if (existingProfile) {
      // Verify the existing profile has the correct merchant ID
      if (existingProfile.merchantId !== merchantId) {
        console.warn('⚠️ Merchant ID mismatch in existing profile. Creating new profile.');
        localStorage.setItem(profileKey, JSON.stringify(defaultProfile));
        return defaultProfile;
      }
      return existingProfile;
    }

    // Save new profile
    localStorage.setItem(profileKey, JSON.stringify(defaultProfile));
    console.log('✅ Created new merchant profile for:', merchantId);
    return defaultProfile;
  }

  /**
   * Get merchant KYC profile
   */
  getMerchantProfile(merchantId) {
    try {
      const profileKey = this.getMerchantKey(merchantId, 'profile');
      const profile = localStorage.getItem(profileKey);
      return profile ? JSON.parse(profile) : null;
    } catch (error) {
      console.error('Error getting merchant profile:', error);
      return null;
    }
  }

  /**
   * Update merchant KYC profile
   */
  updateMerchantProfile(merchantId, updates) {
    try {
      const profile = this.getMerchantProfile(merchantId);
      if (!profile) {
        throw new Error('Merchant profile not found');
      }

      const updatedProfile = {
        ...profile,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      const profileKey = this.getMerchantKey(merchantId, 'profile');
      localStorage.setItem(profileKey, JSON.stringify(updatedProfile));
      return updatedProfile;
    } catch (error) {
      console.error('Error updating merchant profile:', error);
      return null;
    }
  }

  /**
   * Save merchant documents
   */
  saveMerchantDocuments(merchantId, documents) {
    try {
      const documentsKey = this.getMerchantKey(merchantId, 'documents');
      const documentData = {
        merchantId,
        documents: documents.map(doc => ({
          id: doc.id || Date.now() + Math.random(),
          type: doc.type,
          fileName: doc.fileName,
          fileSize: doc.fileSize,
          uploadedAt: doc.uploadedAt || new Date().toISOString(),
          status: doc.status || 'uploaded',
          verificationResult: doc.verificationResult || null
        })),
        lastUpdated: new Date().toISOString()
      };

      localStorage.setItem(documentsKey, JSON.stringify(documentData));
      return documentData;
    } catch (error) {
      console.error('Error saving merchant documents:', error);
      return null;
    }
  }

  /**
   * Get merchant documents
   */
  getMerchantDocuments(merchantId) {
    try {
      const documentsKey = this.getMerchantKey(merchantId, 'documents');
      const documents = localStorage.getItem(documentsKey);
      return documents ? JSON.parse(documents) : { merchantId, documents: [], lastUpdated: null };
    } catch (error) {
      console.error('Error getting merchant documents:', error);
      return { merchantId, documents: [], lastUpdated: null };
    }
  }

  /**
   * Save merchant verification results
   */
  saveMerchantVerification(merchantId, verificationResults) {
    try {
      const verificationKey = this.getMerchantKey(merchantId, 'verification');
      const verificationData = {
        merchantId,
        results: verificationResults,
        timestamp: new Date().toISOString()
      };

      localStorage.setItem(verificationKey, JSON.stringify(verificationData));
      
      // Calculate completion percentage after verification
      let completionPercentage = this.calculateCompletionPercentage(merchantId);
      
      // Update profile status immediately after saving verification
      const statusUpdate = {
        status: verificationResults.status || 'PENDING_REVIEW',
        completionPercentage: completionPercentage,
        lastUpdated: new Date().toISOString(),
        submittedAt: new Date().toISOString()
      };

      // If verification is complete (any status), ensure 100% completion
      if (verificationResults.status === 'APPROVED' || 
          verificationResults.status === 'PENDING_REVIEW' || 
          verificationResults.status === 'REJECTED') {
        statusUpdate.completionPercentage = 100;
      }

      // If approved, set approval date
      if (verificationResults.status === 'APPROVED') {
        statusUpdate.approvedAt = new Date().toISOString();
      }

      this.updateMerchantKycStatus(merchantId, statusUpdate);

      console.log('✅ Verification saved and status updated:', {
        merchantId,
        verificationStatus: verificationResults.status,
        completionPercentage: statusUpdate.completionPercentage,
        timestamp: verificationData.timestamp
      });

      return verificationData;
    } catch (error) {
      console.error('Error saving merchant verification:', error);
      return null;
    }
  }

  /**
   * Get merchant verification results
   */
  getMerchantVerification(merchantId) {
    try {
      const verificationKey = this.getMerchantKey(merchantId, 'verification');
      const verification = localStorage.getItem(verificationKey);
      return verification ? JSON.parse(verification) : null;
    } catch (error) {
      console.error('Error getting merchant verification:', error);
      return null;
    }
  }

  /**
   * Update merchant KYC status
   */
  updateMerchantKycStatus(merchantId, statusUpdates) {
    try {
      const profile = this.getMerchantProfile(merchantId);
      if (!profile) {
        throw new Error('Merchant profile not found');
      }

      const updatedProfile = {
        ...profile,
        kycStatus: {
          ...profile.kycStatus,
          ...statusUpdates,
          lastUpdated: new Date().toISOString()
        },
        updatedAt: new Date().toISOString()
      };

      const profileKey = this.getMerchantKey(merchantId, 'profile');
      localStorage.setItem(profileKey, JSON.stringify(updatedProfile));
      return updatedProfile.kycStatus;
    } catch (error) {
      console.error('Error updating merchant KYC status:', error);
      return null;
    }
  }

  /**
   * Check if merchant is eligible for KYC
   */
  isMerchantEligibleForKyc(userRole) {
    return userRole === 'MERCHANT' || userRole === 'merchant';
  }

  /**
   * Get all merchant profiles (for admin use)
   */
  getAllMerchantProfiles() {
    try {
      const profiles = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          const profile = JSON.parse(localStorage.getItem(key));
          profiles.push(profile);
        }
      }
      return profiles;
    } catch (error) {
      console.error('Error getting all merchant profiles:', error);
      return [];
    }
  }

  /**
   * Delete merchant KYC data (for testing/cleanup)
   */
  deleteMerchantKycData(merchantId) {
    try {
      const profileKey = this.getMerchantKey(merchantId, 'profile');
      const documentsKey = this.getMerchantKey(merchantId, 'documents');
      const verificationKey = this.getMerchantKey(merchantId, 'verification');

      localStorage.removeItem(profileKey);
      localStorage.removeItem(documentsKey);
      localStorage.removeItem(verificationKey);

      console.log('🗑️ Deleted all KYC data for merchant:', merchantId);
      return true;
    } catch (error) {
      console.error('Error deleting merchant KYC data:', error);
      return false;
    }
  }

  /**
   * Validate merchant data integrity
   */
  validateMerchantDataIntegrity(merchantId) {
    try {
      const profile = this.getMerchantProfile(merchantId);
      const documents = this.getMerchantDocuments(merchantId);
      const verification = this.getMerchantVerification(merchantId);

      const issues = [];

      // Check profile integrity
      if (profile && profile.merchantId !== merchantId) {
        issues.push(`Profile merchant ID mismatch: expected ${merchantId}, got ${profile.merchantId}`);
      }

      // Check documents integrity
      if (documents && documents.merchantId !== merchantId) {
        issues.push(`Documents merchant ID mismatch: expected ${merchantId}, got ${documents.merchantId}`);
      }

      // Check verification integrity
      if (verification && verification.merchantId !== merchantId) {
        issues.push(`Verification merchant ID mismatch: expected ${merchantId}, got ${verification.merchantId}`);
      }

      if (issues.length > 0) {
        console.warn('⚠️ Merchant data integrity issues found:', issues);
        return { valid: false, issues };
      }

      return { valid: true, issues: [] };
    } catch (error) {
      console.error('Error validating merchant data integrity:', error);
      return { valid: false, issues: ['Validation error: ' + error.message] };
    }
  }

  /**
   * Fix merchant data integrity issues
   */
  fixMerchantDataIntegrity(merchantId) {
    try {
      const validation = this.validateMerchantDataIntegrity(merchantId);
      
      if (validation.valid) {
        console.log('✅ Merchant data integrity is valid for:', merchantId);
        return true;
      }

      console.log('🔧 Fixing merchant data integrity issues for:', merchantId);
      
      // Delete corrupted data and reinitialize
      this.deleteMerchantKycData(merchantId);
      
      console.log('✅ Merchant data integrity fixed for:', merchantId);
      return true;
    } catch (error) {
      console.error('Error fixing merchant data integrity:', error);
      return false;
    }
  }

  /**
   * Get merchant KYC summary
   */
  getMerchantKycSummary(merchantId) {
    const profile = this.getMerchantProfile(merchantId);
    const documents = this.getMerchantDocuments(merchantId);
    const verification = this.getMerchantVerification(merchantId);

    if (!profile) {
      return null;
    }

    // Check if verification is truly complete (has results AND status indicates completion)
    const hasVerificationResults = verification && verification.results;
    const isVerificationComplete = hasVerificationResults && 
      (profile.kycStatus.status === 'APPROVED' || 
       profile.kycStatus.status === 'PENDING_REVIEW' ||
       profile.kycStatus.status === 'REJECTED');

    return {
      merchantId,
      status: profile.kycStatus.status,
      completionPercentage: profile.kycStatus.completionPercentage,
      documentsCount: documents.documents.length,
      lastUpdated: profile.kycStatus.lastUpdated,
      businessName: profile.businessInfo.businessName,
      fullName: profile.personalInfo.fullName,
      hasVerificationResults: isVerificationComplete,
      verificationData: verification // Include full verification data for debugging
    };
  }

  /**
   * Calculate KYC completion percentage
   */
  calculateCompletionPercentage(merchantId) {
    const profile = this.getMerchantProfile(merchantId);
    const documents = this.getMerchantDocuments(merchantId);
    const verification = this.getMerchantVerification(merchantId);

    if (!profile) return 0;

    // CRITICAL: If verification exists with results, always return 100%
    if (verification && verification.results && 
        (verification.results.status === 'APPROVED' || 
         verification.results.status === 'PENDING_REVIEW' || 
         verification.results.status === 'REJECTED')) {
      console.log('🎯 Verification results found - returning 100% completion');
      return 100;
    }

    // If profile status indicates completion, return 100%
    if (profile.kycStatus.status === 'APPROVED' || 
        profile.kycStatus.status === 'PENDING_REVIEW' ||
        profile.kycStatus.status === 'REJECTED') {
      console.log('🎯 Profile status indicates completion - returning 100%');
      return 100;
    }

    let completed = 0;
    const total = 8; // Total completion criteria

    // Personal info completion (2 points)
    if (profile.personalInfo.fullName && profile.personalInfo.email) completed++;
    if (profile.personalInfo.phone) completed++; // Remove dateOfBirth requirement as it's optional

    // Business info completion (3 points)
    if (profile.businessInfo.businessName && profile.businessInfo.businessType) completed++;
    if (profile.businessInfo.pan && profile.businessInfo.registeredAddress) completed++;
    if (profile.businessInfo.city && profile.businessInfo.state && profile.businessInfo.pincode) completed++;

    // Bank info completion (1 point)
    if (profile.bankInfo.accountNumber && profile.bankInfo.ifscCode && profile.bankInfo.bankName) completed++;

    // Documents completion (1 point) - More flexible document requirement
    const uploadedDocsCount = documents.documents.length;
    if (uploadedDocsCount >= 2) completed++; // At least 2 documents uploaded

    // Verification completion (1 point) - Check if verification results exist AND status indicates completion
    const hasVerificationResults = verification && verification.results;
    const isVerificationComplete = hasVerificationResults && 
      (profile.kycStatus.status === 'APPROVED' || 
       profile.kycStatus.status === 'PENDING_REVIEW' ||
       profile.kycStatus.status === 'REJECTED');
    
    if (isVerificationComplete) completed++;

    const percentage = Math.round((completed / total) * 100);
    
    // Final checks for 100% completion
    if (profile.kycStatus.status === 'APPROVED' || 
        (hasVerificationResults && percentage >= 87)) { // 7/8 = 87.5%, round up to 100% if verification is done
      return 100;
    }
    
    return percentage;
  }

  /**
   * Validate and sync verification status with profile
   */
  validateAndSyncVerificationStatus(merchantId) {
    try {
      const profile = this.getMerchantProfile(merchantId);
      const verification = this.getMerchantVerification(merchantId);
      
      if (!profile) {
        console.warn('⚠️ No profile found for merchant:', merchantId);
        return false;
      }
      
      if (!verification || !verification.results) {
        console.log('ℹ️ No verification results found for merchant:', merchantId);
        return true; // Not an error, just no verification yet
      }
      
      const verificationStatus = verification.results.status;
      const profileStatus = profile.kycStatus.status;
      
      // Check if profile status matches verification results
      if (verificationStatus !== profileStatus) {
        console.log('🔄 Syncing verification status:', {
          merchantId,
          verificationStatus,
          profileStatus,
          syncing: true
        });
        
        // Update profile to match verification
        const statusUpdate = {
          status: verificationStatus,
          completionPercentage: 100,
          lastUpdated: new Date().toISOString(),
          submittedAt: verification.timestamp
        };
        
        if (verificationStatus === 'APPROVED') {
          statusUpdate.approvedAt = verification.timestamp;
        } else if (verificationStatus === 'REJECTED') {
          statusUpdate.rejectedAt = verification.timestamp;
        }
        
        this.updateMerchantKycStatus(merchantId, statusUpdate);
        console.log('✅ Verification status synchronized');
        return true;
      }
      
      console.log('✅ Verification status already synchronized');
      return true;
    } catch (error) {
      console.error('Error validating verification status:', error);
      return false;
    }
  }
}

// Create singleton instance
const merchantKycService = new MerchantKycService();
export default merchantKycService;